/* eslint-disable no-restricted-syntax */
import mapboxgl from "mapbox-gl";
import { useEffect, useMemo, useRef, useState } from "react";

// css
import "mapbox-gl/dist/mapbox-gl.css";
import "./EmissionIntensityMap.css";

// constants
import { MAPBOX_TOKEN } from "utils/constants";

// hooks
import { useNavigate } from "react-router-dom";

// helpers

// components
import { Header } from "components/atoms/Typography";
import { ChartErrorState } from "components/molecules/ErrorStates";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";

import { useMapLayers } from "./EmissionIntensityMap/EmissionIntensityMap.helpers";
import { MapLegend } from "./EmissionIntensityMap/MapLegend";
import useBuildingBarChartAndMapByGroup from "./hooks/useBuildingBarChartAndMapByGroup";

mapboxgl.accessToken = MAPBOX_TOKEN;

interface EmissionIntensityMapProps {
    buildingOrPropertyData: BarChartAndMapData[];
    includeHeader: boolean;
}

export function collectFiltersetData(groupByMetric: GroupByMetric, filtersetUid: string, reportedVsPublished: string) {
    // TODO: useBuildingBarChartAndMapByGroup doesn't appear to take reportedVsPublished, which means that it's returning the wrong data, probably?
    const { data: buildingModels, isLoading, error } = useBuildingBarChartAndMapByGroup(groupByMetric, filtersetUid);

    if (error || isLoading || !buildingModels) {
        return {
            isLoading,
            mapData: [] as BarChartAndMapData[],
            error,
        };
    }

    const mapData = buildingModels.map((bm) => bm as BarChartAndMapData);

    return {
        isLoading,
        mapData,
        error,
    };
}

export function FiltersetBasedEmissionIntensityMap({
    includeHeader,
    groupByMetric,
    filtersetUid,
    reportedVsPublished,
}: {
    includeHeader: boolean;
    groupByMetric: GroupByMetric;
    filtersetUid: string;
    reportedVsPublished: string;
}) {
    const { isLoading, mapData, error } = collectFiltersetData(groupByMetric, filtersetUid, reportedVsPublished);

    if (isLoading || !mapData || mapData.length === 0) return <LoadingSpinner />;
    if (error) return <ChartErrorState />;

    return <EmissionIntensityMap includeHeader={includeHeader} buildingOrPropertyData={mapData} />;
}

export function PropertyBasedEmissionIntensityMap({ properties }: { properties: Property[] }) {
    if (!properties || properties.length === 0) return <LoadingSpinner />;

    const mapData = properties.map((p) => ({
        annualCarbonEmissionIntensityCurrent: p.annualCarbonEmissionIntensityCurrent,
        annualCarbonEmissionsCurrent: p.annualCarbonEmissionIntensityCurrent! * p.grossFloorArea,
        annualEnergyConsumptionCurrent: p.annualEnergyUseIntensityCurrent! * p.grossFloorArea,
        annualEnergyUseIntensityCurrent: p.annualEnergyUseIntensityCurrent,
        latitude: p.buildingModels[0].location.latitude,
        longitude: p.buildingModels[0].location.longitude,
        label: p.propertyName,
    }));
    return <EmissionIntensityMap includeHeader={false} buildingOrPropertyData={mapData} />;
}

export function EmissionIntensityMap({ includeHeader, buildingOrPropertyData }: EmissionIntensityMapProps) {
    const mapIsVisible = true;

    return (
        <div className="emission-intensity-map">
            {includeHeader && (
                <div className="title">
                    <Header size="small">Emission intensity</Header>
                </div>
            )}
            <Map buildingModels={buildingOrPropertyData} visible={mapIsVisible} />
        </div>
    );
}

interface MapProps {
    buildingModels: BarChartAndMapData[];
    visible?: boolean;
}

export function Map({ buildingModels, visible = true }: MapProps) {
    const mapContainer = useRef<HTMLDivElement | null>(null);
    const map = useRef<mapboxgl.Map>(null);
    const [mapHasLoaded, setMapHasLoaded] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!map.current || !visible) return;

        const container = mapContainer.current;
        const resizeObserver = new ResizeObserver(() => {
            map.current?.resize();
        });

        if (container) {
            resizeObserver.observe(container);
        }

        // Clean up the observer when component unmounts or visibility changes - important !
        // eslint-disable-next-line consistent-return
        return () => {
            if (container) {
                resizeObserver.unobserve(container);
            }
            resizeObserver.disconnect();
        };
    }, [visible]);

    const { minEmissionIntensity, maxEmissionIntensity } = useMemo(() => {
        const intensities = buildingModels.map(
            ({ annualCarbonEmissionIntensityCurrent }) => annualCarbonEmissionIntensityCurrent
        );
        const minEmissionIntensity = Math.min(...intensities);
        const maxEmissionIntensity = Math.max(...intensities);
        return { minEmissionIntensity, maxEmissionIntensity };
    }, [buildingModels]);

    useMapLayers(
        buildingModels,
        map,
        mapContainer,
        setMapHasLoaded,
        minEmissionIntensity,
        maxEmissionIntensity,
        navigate
    );

    return (
        <div ref={mapContainer} className="map-container">
            {mapHasLoaded && <MapLegend scaleMin={minEmissionIntensity} scaleMax={maxEmissionIntensity} />}
        </div>
    );
}
